import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { KycFormInput, OcrPassportData } from "../../types/kyc";
import { postOcrPassport } from "../../api/kycApi";
import type { CountryModel } from "../../types/common";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhotoCameraBackOutlinedIcon from "@mui/icons-material/PhotoCameraBackOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorIcon from "@mui/icons-material/Error";
import DocumentScannerOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined"; // ADDED
import { useCountries } from "../../hooks/useCountries";
import MRZCameraModal from "../MRZCameraModal"; // ADDED
import { BACKEND_COUNTRIES } from "../../constants/countries";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

// Native <input type="date"> renders its year using the OS regional/calendar
// setting, so Thai (Buddhist) locales show BE instead of AD. The MUI DatePicker
// below is pinned to the Gregorian "en" locale so every computer displays AD.
const DATE_DISPLAY_FORMAT = "DD/MM/YYYY";

interface FormKYCProps {
  formData: KycFormInput;
  onChange: (field: keyof KycFormInput, value: any) => void;
  onSubmit: (data: KycFormInput) => void;
  disableAll: boolean;
  validationErrors?: Record<string, string>;
  onValidationChange?: (
    isValid: boolean,
    errors: Record<string, string>,
  ) => void;
}

// --- Validation Functions ---------------------------------------------------------------
const validateKycForm = (data: KycFormInput): Record<string, string> => {
  const errors: Record<string, string> = {};

  // Required text fields
  if (!data.name?.trim()) errors.name = "First name is required";
  if (!data.surname?.trim()) errors.surname = "Last name is required";
  if (!data.birth_day) errors.birth_day = "Date of birth is required";
  if (!data.gender) errors.gender = "Gender is required";
  if (!data.country) errors.country = "Country of residency is required";
  if (!data.nation) errors.nation = "Nationality is required";
  if (!data.passport_no?.trim())
    errors.passport_no = "Passport number is required";
  if (!data.expiry_date)
    errors.expiry_date = "Passport expiry date is required";

  // File validations
  if (!data.doc_photo) errors.doc_photo = "Passport photo is required";
  else if (!data.doc_photo_url)
    errors.doc_photo =
      "Passport photo upload is still processing or failed. Please choose the passport photo again.";
  else if (!data.job_id)
    errors.doc_photo =
      "Passport OCR job is still processing or failed. Please choose the passport photo again.";
  // create-kyc no longer accepts personal_photo
  // if (!data.personal_photo)
  //   errors.personal_photo = "Personal photo is required";

  // Date validations
  if (data.birth_day) {
    const birthDate = new Date(data.birth_day);
    const today = new Date();
    // const age = today.getFullYear() - birthDate.getFullYear();
    // if (age < 18) errors.birth_day = "Must be at least 18 years old";
    if (birthDate > today)
      errors.birth_day = "Birth date cannot be in the future";
  }

  if (data.expiry_date) {
    const expiryDate = new Date(data.expiry_date);
    const today = new Date();
    if (expiryDate <= today)
      errors.expiry_date = "Passport must not be expired";
  }

  // Passport number format validation
  if (data.passport_no && data.passport_no.length < 6) {
    errors.passport_no = "Passport number must be at least 6 characters";
  }

  return errors;
};

// --- Helpers ---------------------------------------------------------------
const countryCodeToFlag = (isoCode: string) =>
  typeof String.fromCodePoint !== "undefined"
    ? isoCode
        .toUpperCase()
        .replace(/./g, (char) =>
          String.fromCodePoint(127397 + char.charCodeAt(0)),
        )
    : isoCode;

/** Normalize OCR / API date strings to `YYYY-MM-DD` for MUI date inputs. */
const normalizeDobForDateInput = (raw: string): string => {
  const s = raw?.trim() ?? "";
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const dm = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(s);
  if (dm) {
    const [, d, mo, y] = dm;
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const t = new Date(s);
  return Number.isNaN(t.getTime()) ? "" : t.toISOString().slice(0, 10);
};

const mapOcrGenderToForm = (raw: string): string => {
  const u = (raw ?? "").toLowerCase().trim();
  if (u === "m" || u === "male") return "male";
  if (u === "f" || u === "female") return "female";
  return "";
};

const resolveCountryCode = (raw: string): string => {
  const r = raw?.trim() ?? "";
  if (!r) return "";
  const upper = r.toUpperCase();

  // 1. Try to match by alpha3 (from OCR)
  const byAlpha3 = BACKEND_COUNTRIES.find(
    (c) => c.alpha3.toUpperCase() === upper,
  );
  if (byAlpha3) return byAlpha3.alpha2; // Return alpha2

  // 2. Try to match by alpha2 (if user types it or for existing data)
  const byAlpha2 = BACKEND_COUNTRIES.find(
    (c) => c.alpha2.toUpperCase() === upper,
  );
  if (byAlpha2) return byAlpha2.alpha2; // Return alpha2

  // 3. Try to match by name
  const byName = BACKEND_COUNTRIES.find((c) => c.name.toUpperCase() === upper);
  if (byName) return byName.alpha2; // Return alpha2

  return "";
};

const applyOcrPassportToFormFields = (
  ocr: OcrPassportData,
  onChange: (field: keyof KycFormInput, value: any) => void,
): { pendingIssuingCountry?: string; pendingNationality?: string } => {
  if (ocr.name?.trim()) onChange("name", ocr.name.trim());
  if (ocr.surname?.trim()) onChange("surname", ocr.surname.trim());

  const birth = normalizeDobForDateInput(ocr.date_of_birth);
  if (birth) onChange("birth_day", birth);

  const expiry = normalizeDobForDateInput(ocr.expiry_date);
  if (expiry) onChange("expiry_date", expiry);

  const gender = mapOcrGenderToForm(ocr.gender);
  if (gender) onChange("gender", gender);

  if (ocr.passport_number?.trim()) {
    onChange("passport_no", ocr.passport_number.trim().toUpperCase());
  }

  let pendingIssuingCountry: string | undefined;
  let pendingNationality: string | undefined;

  // Map Issuing Country
  const issuingCountryCode = resolveCountryCode(ocr.issuing_country);
  if (issuingCountryCode) {
    onChange("country", issuingCountryCode);
  } else {
    pendingIssuingCountry = ocr.issuing_country?.trim();
  }

  // Map Nationality
  const nationalityCode = resolveCountryCode(ocr.nationality);
  if (nationalityCode) {
    onChange("nation", nationalityCode);
  } else {
    pendingNationality = ocr.nationality?.trim();
  }

  return { pendingIssuingCountry, pendingNationality };
};

const ORIENTATION_FIXABLE_IMAGE = /^image\/(jpeg|jpg|png|webp)$/i;

// Desktop Chromium silently drops the file-input click when `capture` is set
// but no camera is available, so we only request capture on touch devices.
const supportsNativeCameraCapture = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined")
    return false;
  return (
    "ontouchstart" in window ||
    (typeof navigator.maxTouchPoints === "number" &&
      navigator.maxTouchPoints > 0)
  );
};

/**
 * Phone photos often store pixels rotated with an EXIF Orientation tag; `<img>` may
 * look correct while the raw `File` is still rotated. Decode with `imageOrientation:
 * 'from-image'` and re-encode so pixel data matches what the user sees (helps OCR
 * and uploads). Falls back to the original file if decoding fails.
 */
async function normalizeImageOrientationToFile(file: File): Promise<File> {
  if (!ORIENTATION_FIXABLE_IMAGE.test(file.type)) return file;

  try {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    } as unknown as ImageBitmapOptions);
    const w = bitmap.width;
    const h = bitmap.height;
    if (!w || !h) {
      bitmap.close();
      return file;
    }

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    const outMime =
      file.type === "image/webp"
        ? "image/webp"
        : file.type === "image/png"
          ? "image/png"
          : "image/jpeg";

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(
        (b) => resolve(b),
        outMime,
        outMime === "image/jpeg" ? 0.92 : undefined,
      );
    });
    if (!blob || blob.size === 0) return file;

    const base = file.name.replace(/\.[^/.]+$/, "") || "image";
    const ext =
      outMime === "image/png"
        ? ".png"
        : outMime === "image/webp"
          ? ".webp"
          : ".jpg";

    return new File([blob], `${base}${ext}`, {
      type: outMime,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}

async function getImageFileDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  if (!file.type.startsWith("image/")) {
    return { width: 0, height: 0 };
  }
  if (ORIENTATION_FIXABLE_IMAGE.test(file.type)) {
    try {
      const bitmap = await createImageBitmap(file, {
        imageOrientation: "from-image",
      } as unknown as ImageBitmapOptions);
      const width = bitmap.width;
      const height = bitmap.height;
      bitmap.close();
      if (width > 0 && height > 0) return { width, height };
    } catch {
      /* fall through */
    }
  }
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });
}

/** ขั้นต่ำด้านสั้น (px) — ต่ำกว่านี้ OCR มักอ่านยาก */
const PASSPORT_SCAN_MIN_SHORT_SIDE = 600;
/** สัดส่วนยาว/สั้น — หน้า passport จริงมักอยู่ช่วงนี้ (ถ่ายเต็มหน้า) */
const PASSPORT_SCAN_RATIO_MIN = 1.15;
const PASSPORT_SCAN_RATIO_MAX = 2.55;

function validatePassportScanDimensions(
  width: number,
  height: number,
): string | null {
  if (width <= 0 || height <= 0) return null;
  const shortSide = Math.min(width, height);
  const longSide = Math.max(width, height);
  const ratio = longSide / shortSide;
  const parts: string[] = [];
  if (shortSide < PASSPORT_SCAN_MIN_SHORT_SIDE) {
    parts.push(
      `ความละเอียดต่ำเกินไป (${width}×${height}px) — แนะนำด้านสั้นอย่างน้อย ${PASSPORT_SCAN_MIN_SHORT_SIDE}px เพื่อให้ OCR อ่านได้ชัด`,
    );
  }
  if (ratio < PASSPORT_SCAN_RATIO_MIN) {
    parts.push(
      "สัดส่วนรูปใกล้สี่เหลี่ยมจัตุรัส — ลองถ่ายให้เห็นหน้า passport เต็มกรอบ (แนวตั้งหรือแนวนอน)",
    );
  }
  if (ratio > PASSPORT_SCAN_RATIO_MAX) {
    parts.push(
      "สัดส่วนรูปผิดปกติ (แคบหรือเตี้ยมาก) — ตรวจสอบว่าเห็นหน้าเอกสารเต็มพอสมควร",
    );
  }
  return parts.length > 0 ? parts.join(" ") : null;
}

const PREVIEW_MAX_HEIGHT_PX = 400;

const DropArea = styled(Box)(({ theme }) => ({
  border: `1.5px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(2.5),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1.5),
  transition: "background-color 120ms ease, border-color 120ms ease",
  cursor: "pointer",
  textAlign: "center",
  [theme.breakpoints.up("sm")]: { padding: theme.spacing(3) },
  "&.dragover": {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.text.secondary,
  },
  "&.error": {
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.error.light + "10",
  },
}));

// A reusable image upload field with drag & drop, preview, and validation
function ImageUploadCard({
  label,
  name,
  value,
  disabled,
  onFileChange,
  error,
  image,
  accept = ["image/jpeg", "image/png", "image/webp"],
  cameraCapture = "environment",
  fixExifOrientation = true,
  dimensionValidator,
  onMRZScan, // ADDED
}: {
  label: string;
  name: keyof KycFormInput;
  value: File | null;
  disabled?: boolean;
  onFileChange: (file: File | null) => void;
  error?: string;
  image?: string;
  accept?: string[];
  /** `environment` = rear camera (ID), `user` = front camera (selfie). */
  cameraCapture?: "environment" | "user";
  /** When true, JPEG/PNG/WebP are re-encoded so EXIF orientation is baked into pixels. */
  fixExifOrientation?: boolean;
  /** หลังอัปโหลด: ตรวจขนาด/สัดส่วน คืนข้อความเตือนหรือ null */
  dimensionValidator?: (width: number, height: number) => string | null;
  onMRZScan?: () => void; // ADDED
}) {
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const orientationFixGen = useRef(0);
  const measureGen = useRef(0);
  const [isFixingOrientation, setIsFixingOrientation] = useState(false);
  const [fileError, setFileError] = useState<string>("");
  const [dimensionHint, setDimensionHint] = useState<string | null>(null);
  const [previewSize, setPreviewSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [isMeasuringPreview, setIsMeasuringPreview] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [sourceMenuAnchor, setSourceMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const sourceMenuOpen = Boolean(sourceMenuAnchor);

  const [normalizedPreviewFile, setNormalizedPreviewFile] =
    useState<File | null>(null);

  const previewUrl = useMemo(() => {
    const fileToPreview = normalizedPreviewFile || value;
    return fileToPreview ? URL.createObjectURL(fileToPreview) : "";
  }, [normalizedPreviewFile, value]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!value) {
      setNormalizedPreviewFile(null);
      orientationFixGen.current += 1; // Cancel any pending normalization
      setIsFixingOrientation(false);
    }
  }, [value]);

  useEffect(() => {
    const fileToMeasure = normalizedPreviewFile || value;
    if (!fileToMeasure) {
      measureGen.current += 1;
      setPreviewSize(null);
      setDimensionHint(null);
      setIsMeasuringPreview(false);
      return;
    }

    const gen = ++measureGen.current;
    setIsMeasuringPreview(true);
    setDimensionHint(null);

    void getImageFileDimensions(fileToMeasure).then(({ width, height }) => {
      if (gen !== measureGen.current) return;
      setPreviewSize(width > 0 && height > 0 ? { width, height } : null);
      if (dimensionValidator && width > 0 && height > 0) {
        setDimensionHint(dimensionValidator(width, height));
      } else {
        setDimensionHint(null);
      }
      setIsMeasuringPreview(false);
    });
  }, [normalizedPreviewFile, value, dimensionValidator]);

  // const validateFile = (file: File) => {
  //   if (!accept.includes(file.type)) {
  //     return `Unsupported type: ${file.type}. Allowed: ${accept
  //       .map((t) => t.split("/")[1].toUpperCase())
  //       .join(", ")}`;
  //   }
  //   const maxBytes = maxSizeMB * 1024 * 1024;
  //   if (file.size > maxBytes) {
  //     return `File too large. Max ${maxSizeMB} MB`;
  //   }
  //   return "";
  // };

  const openSourceMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (busy) return;
    setSourceMenuAnchor(event.currentTarget);
  };

  const closeSourceMenu = () => setSourceMenuAnchor(null);

  const triggerGalleryPicker = () => {
    closeSourceMenu();
    window.setTimeout(() => galleryInputRef.current?.click(), 0);
  };

  const triggerCameraPicker = () => {
    closeSourceMenu();
    window.setTimeout(() => cameraInputRef.current?.click(), 0);
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    e.target.value = "";
  };

  const handleFiles = useCallback(
    (files?: FileList | null) => {
      if (!files || files.length === 0) return;
      const f = files[0];
      onFileChange(f); // Always pass the original file to the parent

      // Process for preview if orientation fixing is enabled
      if (fixExifOrientation && ORIENTATION_FIXABLE_IMAGE.test(f.type)) {
        const gen = ++orientationFixGen.current;
        setIsFixingOrientation(true);
        void normalizeImageOrientationToFile(f)
          .then((normalized) => {
            if (gen !== orientationFixGen.current) return;
            setNormalizedPreviewFile(normalized); // Store normalized file for preview
          })
          .finally(() => {
            if (gen === orientationFixGen.current)
              setIsFixingOrientation(false);
          });
      } else {
        setNormalizedPreviewFile(null); // No normalization needed for preview
      }
    },
    [fixExifOrientation, onFileChange],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const onDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDragOver) setIsDragOver(true);
    },
    [isDragOver],
  );

  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const busy = disabled || isFixingOrientation;
  const hasError = !!(error || fileError);

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        borderColor: hasError ? "error.main" : undefined,
        height: "100%",
      }}
    >
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <PhotoCameraBackOutlinedIcon
              fontSize="small"
              color={hasError ? "error" : "inherit"}
            />
            <Typography fontWeight={600} color={hasError ? "error" : "inherit"}>
              {label}
            </Typography>
            <Tooltip title="JPG/PNG/WebP up to 7MB. Clear background preferred for ID.">
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
            {hasError && <ErrorIcon fontSize="small" color="error" />}
            {isFixingOrientation && (
              <CircularProgress
                size={16}
                aria-label="Fixing image orientation"
              />
            )}
          </Stack>
          {value && (
            <Tooltip title="Remove file">
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    orientationFixGen.current += 1;
                    setIsFixingOrientation(false);
                    onFileChange(null);
                  }}
                  disabled={disabled}
                  aria-label={`remove ${label}`}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
        {/* ADDED — In-app passport MRZ scanner entry point */}
        {onMRZScan && (
          <Stack direction="row" mb={1}>
            <Button
              onClick={onMRZScan}
              disabled={busy}
              startIcon={<DocumentScannerOutlinedIcon />}
              variant="outlined"
              size="small"
            >
              Scan passport
            </Button>
          </Stack>
        )}
        {value ? (
          <Box>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 180,
                maxHeight: PREVIEW_MAX_HEIGHT_PX + 32,
                bgcolor: "action.hover",
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                overflow: "hidden",
                position: "relative",
              }}
            >
              {(isMeasuringPreview || isFixingOrientation) && (
                <CircularProgress
                  size={28}
                  sx={{ position: "absolute" }}
                  aria-label="Loading preview"
                />
              )}
              <Box
                component="img"
                src={previewUrl}
                alt={`${label} preview`}
                sx={{
                  width: "auto",
                  height: "auto",
                  maxWidth: "100%",
                  maxHeight: PREVIEW_MAX_HEIGHT_PX,
                  objectFit: "contain",
                  display: "block",
                  opacity: isFixingOrientation ? 0.35 : 1,
                  transition: "opacity 0.15s ease",
                }}
              />
            </Box>
            {previewSize && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.75}
              >
                Image size: {previewSize.width}×{previewSize.height}px
                {previewSize.width > 0 &&
                  previewSize.height > 0 &&
                  ` · Ratio ${(Math.max(previewSize.width, previewSize.height) / Math.min(previewSize.width, previewSize.height)).toFixed(2)}:1`}
              </Typography>
            )}
            {dimensionHint && (
              <Alert
                severity="warning"
                sx={{ mt: 1 }}
                onClose={() => setDimensionHint(null)}
              >
                <Typography variant="body2">{dimensionHint}</Typography>
              </Alert>
            )}
            <Stack direction="row" justifyContent="flex-end" mt={1}>
              <Button
                onClick={openSourceMenu}
                disabled={busy}
                startIcon={<CloudUploadOutlinedIcon />}
              >
                Replace
              </Button>
            </Stack>
          </Box>
        ) : (
          <DropArea
            role="button"
            aria-label={`${label} upload area`}
            className={`${isDragOver ? "dragover" : ""} ${
              hasError ? "error" : ""
            }`}
            onClick={openSourceMenu}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            sx={{ bgcolor: busy ? "action.disabledBackground" : undefined }}
          >
            <CloudUploadOutlinedIcon color={hasError ? "error" : "inherit"} />
            <Box>
              <Typography
                variant="body2"
                fontWeight={600}
                color={hasError ? "error" : "inherit"}
              >
                Drag & drop or{" "}
                <Typography component="span" fontWeight={700}>
                  camera / gallery
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                JPG, PNG, or WebP — up to MB
              </Typography>
            </Box>
          </DropArea>
        )}
        {(error || fileError) && (
          <Typography mt={1} variant="caption" color="error">
            {error || fileError}
          </Typography>
        )}
        <Menu
          anchorEl={sourceMenuAnchor}
          open={sourceMenuOpen}
          onClose={closeSourceMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          {/* CHANGED — when onMRZScan is provided, the in-app scanner IS the camera path; remove the native capture entry */}
          {!onMRZScan && (
            <MenuItem onClick={triggerCameraPicker} disabled={busy}>
              <ListItemIcon>
                <PhotoCameraOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Take photo"
                secondary="Use camera"
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </MenuItem>
          )}
          <MenuItem onClick={triggerGalleryPicker} disabled={busy}>
            <ListItemIcon>
              <PhotoLibraryOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Choose from gallery"
              secondary="Photos & files"
              secondaryTypographyProps={{ variant: "caption" }}
            />
          </MenuItem>
        </Menu>
        <input
          ref={galleryInputRef}
          type="file"
          name={name as string}
          accept={accept.join(",")}
          onChange={onFileInputChange}
          style={{ display: "none" }}
          disabled={busy}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept={accept.join(",")}
          capture={supportsNativeCameraCapture() ? cameraCapture : undefined}
          onChange={onFileInputChange}
          style={{ display: "none" }}
          disabled={busy}
          aria-hidden
          tabIndex={-1}
        />
        <DropArea>
          {/* <Box>dddd</Box> */}
          <img
            src={image}
            alt="Logo"
            style={{
              width: "100%",
              height: "auto",
              maxWidth: 80,
              objectFit: "contain",
            }}
          />
        </DropArea>
      </CardContent>
    </Card>
  );
}

// --- Main Form -------------------------------------------------------------
const FormKYC: React.FC<FormKYCProps> = ({
  formData,
  onChange,
  disableAll,
  validationErrors = {},
  onValidationChange,
}) => {
  const { data: countries = [], isLoading: isCountriesLoading } =
    useCountries();
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [passportOcrLoading, setPassportOcrLoading] = useState(false);
  const [passportOcrError, setPassportOcrError] = useState<string | null>(null);
  const [mrzOpen, setMrzOpen] = useState(false); // ADDED
  const passportOcrRequestId = useRef(0);
  const pendingIssuingCountryRef = useRef<string | null>(null);
  const pendingNationalityRef = useRef<string | null>(null);

  const handleDocPhotoChange = useCallback(
    async (file: File | null) => {
      onChange("doc_photo", file);
      onChange("doc_photo_url", "");
      onChange("job_id", "");
      setPassportOcrError(null);

      if (!file) {
        passportOcrRequestId.current += 1;
        pendingIssuingCountryRef.current = null;
        pendingNationalityRef.current = null;
        setPassportOcrLoading(false);
        return;
      }

      const requestId = ++passportOcrRequestId.current;
      setPassportOcrLoading(true);

      try {
        const ocr = await postOcrPassport(file);

        if (requestId !== passportOcrRequestId.current) return;
        onChange("doc_photo_url", ocr.data.image_url || "");
        onChange("job_id", ocr.data.job_id || "");
        const { pendingIssuingCountry, pendingNationality } =
          applyOcrPassportToFormFields(ocr.data, onChange);
        pendingIssuingCountryRef.current = pendingIssuingCountry ?? null;
        pendingNationalityRef.current = pendingNationality ?? null;
      } catch {
        if (requestId !== passportOcrRequestId.current) return;
        setPassportOcrError(
          "Could not read passport details from this image. Please enter your information manually.",
        );
      } finally {
        if (requestId === passportOcrRequestId.current) {
          setPassportOcrLoading(false);
        }
      }
    },
    [countries, onChange],
  );

  useEffect(() => {
    // Resolve pending Issuing Country
    const pendingIssuing = pendingIssuingCountryRef.current;
    if (pendingIssuing) {
      const code = resolveCountryCode(pendingIssuing);
      if (code) {
        pendingIssuingCountryRef.current = null;
        onChange("country", code);
      }
    }

    // Resolve pending Nationality
    const pendingNation = pendingNationalityRef.current;
    if (pendingNation) {
      const code = resolveCountryCode(pendingNation);
      if (code) {
        pendingNationalityRef.current = null;
        onChange("nation", code);
      }
    }
  }, [onChange]);

  // Validate form whenever formData changes
  useEffect(() => {
    const errors = validateKycForm(formData);
    setLocalErrors(errors);
    if (onValidationChange) {
      onValidationChange(Object.keys(errors).length === 0, errors);
    }
  }, [formData, onValidationChange]);

  // Merge validation errors from props and local validation
  const allErrors = { ...localErrors, ...validationErrors };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    let fieldValue: any = value;
    if (type === "file" && e.target instanceof HTMLInputElement) {
      fieldValue = e.target.files?.[0] ?? null;
    }
    onChange(name as keyof KycFormInput, fieldValue);
  };

  // Check if form is complete and valid
  const isFormValid = Object.keys(allErrors).length === 0;
  // const hasRequiredFields = !!(
  //   formData.name?.trim() &&
  //   formData.surname?.trim() &&
  //   formData.birth_day &&
  //   formData.gender &&
  //   formData.country &&
  //   formData.nation &&
  //   formData.passport_no?.trim() &&
  //   formData.expiry_date &&
  //   formData.doc_photo &&
  //   formData.personal_photo
  // );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
      <Stack gap={2}>
        {Object.keys(allErrors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Please fix the following errors:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {Object.entries(allErrors).map(([field, error]) => (
                <li key={field}>
                  <Typography variant="caption">{error}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <Stack spacing={1}>
              {passportOcrLoading && (
                <Alert
                  severity="info"
                  icon={<CircularProgress color="inherit" size={18} />}
                >
                  Reading passport details from your image…
                </Alert>
              )}
              {passportOcrError && (
                <Alert
                  severity="warning"
                  onClose={() => setPassportOcrError(null)}
                >
                  {passportOcrError}
                </Alert>
              )}
              <ImageUploadCard
                label="Passport Photo (ID page)"
                name="doc_photo"
                value={formData.doc_photo}
                disabled={disableAll || passportOcrLoading}
                error={allErrors.doc_photo}
                image="kyc-guide/passport.png"
                dimensionValidator={validatePassportScanDimensions}
                onFileChange={handleDocPhotoChange}
                onMRZScan={() => setMrzOpen(true)} // ADDED
              />
            </Stack>
          </Grid>
          {/* create-kyc no longer accepts personal_photo
          <Grid item xs={12} md={6}>
            <ImageUploadCard
              label="Personal Portrait (Selfie)"
              name="personal_photo"
              value={formData.personal_photo}
              disabled={disableAll}
              error={allErrors.personal_photo}
              image="kyc-guide/personal-doc.png"
              cameraCapture="user"
              onFileChange={(file) => onChange("personal_photo", file)}
            />
          </Grid>
          */}
          <Grid item xs={12} sm={6}>
            <TextField
              name="name"
              label="First Name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              disabled={disableAll}
              required
              error={!!allErrors.name}
              helperText={allErrors.name}
              autoComplete="given-name"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="surname"
              label="Last Name"
              value={formData.surname}
              onChange={handleInputChange}
              fullWidth
              disabled={disableAll}
              required
              error={!!allErrors.surname}
              helperText={allErrors.surname}
              autoComplete="family-name"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Date of Birth"
              format={DATE_DISPLAY_FORMAT}
              value={formData.birth_day ? dayjs(formData.birth_day) : null}
              onChange={(d) =>
                onChange(
                  "birth_day",
                  d && d.isValid() ? d.format("YYYY-MM-DD") : "",
                )
              }
              disabled={disableAll}
              slotProps={{
                textField: {
                  name: "birth_day",
                  fullWidth: true,
                  required: true,
                  error: !!allErrors.birth_day,
                  helperText: allErrors.birth_day,
                  autoComplete: "bday",
                  InputLabelProps: { shrink: true },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack spacing={0.5}>
              <Typography
                variant="caption"
                color={allErrors.gender ? "error" : "text.secondary"}
              >
                Gender *
              </Typography>
              <ToggleButtonGroup
                value={formData.gender || null}
                exclusive
                onChange={(_, val) => val && onChange("gender", val)}
                disabled={disableAll}
                size="small"
                aria-label="gender"
                sx={{
                  "& .MuiToggleButton-root": {
                    borderColor: allErrors.gender ? "error.main" : undefined,
                  },
                }}
              >
                <ToggleButton value="male">Male</ToggleButton>
                <ToggleButton value="female">Female</ToggleButton>
              </ToggleButtonGroup>
              {allErrors.gender && (
                <Typography variant="caption" color="error">
                  {allErrors.gender}
                </Typography>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={countries}
              getOptionLabel={(option: any) => option.name}
              loading={isCountriesLoading}
              isOptionEqualToValue={(o: any, v: any) => o.code === v.code}
              value={
                countries.find((c: any) => c.code === formData.country) || null
              }
              onChange={(_, newValue) => {
                onChange("country", newValue ? newValue.code : "");
              }}
              disabled={disableAll}
              renderOption={(props, option: any) => (
                <li {...props}>
                  <Box component="span" mr={1}>
                    {countryCodeToFlag(option.code)}
                  </Box>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Issuing Country"
                  fullWidth
                  required
                  error={!!allErrors.country}
                  helperText={allErrors.country}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={countries}
              getOptionLabel={(option: any) => option.name}
              loading={isCountriesLoading}
              isOptionEqualToValue={(o: any, v: any) => o.code === v.code}
              value={
                countries.find((c: any) => c.code === formData.nation) || null
              }
              onChange={(_, newValue) => {
                onChange("nation", newValue ? newValue.code : "");
              }}
              disabled={disableAll}
              renderOption={(props, option: any) => (
                <li {...props}>
                  <Box component="span" mr={1}>
                    {countryCodeToFlag(option.code)}
                  </Box>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Nationality"
                  fullWidth
                  required
                  error={!!allErrors.nation}
                  helperText={allErrors.nation}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="passport_no"
              label="Passport Number"
              value={formData.passport_no}
              onChange={(e) =>
                onChange("passport_no", e.target.value.toUpperCase())
              }
              fullWidth
              disabled={disableAll}
              required
              error={!!allErrors.passport_no}
              helperText={allErrors.passport_no}
              autoComplete="off"
              inputProps={{ inputMode: "text", pattern: "[A-Za-z0-9]*" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Passport Expiry Date"
              format={DATE_DISPLAY_FORMAT}
              value={formData.expiry_date ? dayjs(formData.expiry_date) : null}
              onChange={(d) =>
                onChange(
                  "expiry_date",
                  d && d.isValid() ? d.format("YYYY-MM-DD") : "",
                )
              }
              disabled={disableAll}
              slotProps={{
                textField: {
                  name: "expiry_date",
                  fullWidth: true,
                  required: true,
                  error: !!allErrors.expiry_date,
                  helperText: allErrors.expiry_date,
                  autoComplete: "off",
                  InputLabelProps: { shrink: true },
                },
              }}
            />
          </Grid>
        </Grid>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={isFormValid ? "Ready to Submit" : "Incomplete"}
              color={isFormValid ? "success" : "warning"}
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
              {isFormValid
                ? "All required fields completed. Your data is encrypted in transit."
                : "Please complete all required fields."}
            </Typography>
          </Stack>
        </Stack>

        {/* ADDED — Fullscreen MRZ camera scanner; routes its cropped File through the existing OCR pipeline */}
        <MRZCameraModal
          open={mrzOpen}
          onClose={() => setMrzOpen(false)}
          onCapture={(file) => {
            setMrzOpen(false);
            void handleDocPhotoChange(file);
          }}
        />
      </Stack>
    </LocalizationProvider>
  );
};

export default FormKYC;
